import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  FolderTree,
  Search,
  Info,
  Settings,
  Activity,
  Terminal,
  AlertTriangle,
} from 'lucide-react'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { cn } from '@/lib/utils'

interface CollapsedStripProps {
  position: 'left' | 'right' | 'bottom'
  onExpand: () => void
}

export function CollapsedStrip({ position, onExpand }: CollapsedStripProps) {
  const { leftTab, setLeftTab, bottomTab, setBottomTab } = useWorkspaceStore()

  if (position === 'left') {
    return (
      <div className="h-full w-12 bg-surface-sunken border-r border-border flex flex-col items-center py-2 gap-1">
        {/* Expand button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onExpand}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Expand panel</TooltipContent>
        </Tooltip>

        <div className="w-8 h-px bg-border my-1" />

        {/* Quick access icons */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={leftTab === 'explorer' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                setLeftTab('explorer')
                onExpand()
              }}
            >
              <FolderTree className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Project Explorer</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={leftTab === 'library' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                setLeftTab('library')
                onExpand()
              }}
            >
              <Search className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Component Library</TooltipContent>
        </Tooltip>
      </div>
    )
  }

  if (position === 'right') {
    return (
      <div className="h-full w-12 bg-surface-sunken border-l border-border flex flex-col items-center py-2 gap-1">
        {/* Expand button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onExpand}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Expand panel</TooltipContent>
        </Tooltip>

        <div className="w-8 h-px bg-border my-1" />

        {/* Quick access icons */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onExpand}>
              <Info className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Node Details</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onExpand}>
              <Settings className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Properties</TooltipContent>
        </Tooltip>
      </div>
    )
  }

  // Bottom strip
  return (
    <div className="h-9 w-full bg-surface-sunken border-t border-border flex items-center px-2 gap-1">
      {/* Expand button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onExpand}>
            <ChevronUp className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Expand panel</TooltipContent>
      </Tooltip>

      <div className="w-px h-5 bg-border mx-1" />

      {/* Quick access icons */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={bottomTab === 'feed' ? 'secondary' : 'ghost'}
            size="sm"
            className={cn('h-7 gap-1.5 text-xs', bottomTab === 'feed' && 'bg-secondary')}
            onClick={() => {
              setBottomTab('feed')
              onExpand()
            }}
          >
            <Activity className="h-3.5 w-3.5" />
            Activity
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Activity Feed</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={bottomTab === 'console' ? 'secondary' : 'ghost'}
            size="sm"
            className={cn('h-7 gap-1.5 text-xs', bottomTab === 'console' && 'bg-secondary')}
            onClick={() => {
              setBottomTab('console')
              onExpand()
            }}
          >
            <Terminal className="h-3.5 w-3.5" />
            Console
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Calculation Console</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={bottomTab === 'validation' ? 'secondary' : 'ghost'}
            size="sm"
            className={cn('h-7 gap-1.5 text-xs', bottomTab === 'validation' && 'bg-secondary')}
            onClick={() => {
              setBottomTab('validation')
              onExpand()
            }}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Validation
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Validation Warnings</TooltipContent>
      </Tooltip>
    </div>
  )
}
